import {
  entityLabel,
  eventDayClock,
  historyEntities,
  eventSentence,
  eventTime,
  fieldChanges,
  parseRow,
  whoDidIt,
  type AuditEvent,
  type FieldChange,
  type HistoryNames,
  undoBodyFor,
} from '../history'
import { api, dismiss, getQueue, mutate } from '../api'
import { getMe } from '../me'
import { routeGen } from '../route-state'
import { clear, dayLabel, el, loadError, money, todayISO, toast } from '../ui'
import { lineDrawing, type Drawing } from '../picture-tiles'
import '../history-page.css'

// History — the audit view of every change (kimi S-3's surface, made visible
// in the app). Rules here:
//   - Undo only on the newest change to a visit made by a person in the last
//     day (history.ts undoBodyFor, Liam 9 Oct 2026): one guarded save, the
//     server doing the rest as for any change. Still no restore of visits
//     made or removed, or of customer changes (a cross-entity restore needs
//     its own careful design, not a button that half-works);
//   - text nodes only, values formatted the way the sheets read them
//     (money, dates, yes/no) — no raw JSON, no request hashes or
//     idempotency keys anywhere a person is asked to look;
//   - never cached: this view names customers and money, so it is not
//     written to the persistent read cache other screens use;
//   - stale loads are abandoned, not rendered (routeGen + a load sequence,
//     the same contract as the editable screens).
// Drawn as design A (Liam, 23 Oct 2026: "same for history"; drawings https://claude.ai/artifact/ExLzcBz3Skbk3Ph7721fUX):
// the changes under their day, one line each with a small picture of what kind of change it was, the same change made
// three or more times in a row folded into one line that opens to every one of them (each with its own values and
// Undo), the explanation under About History. On a phone a change opens under itself; on a computer it opens on the
// right.

const PAGE_LIMIT = 50

interface HistoryFilters {
  entity: string
  recordId: string
  since: string
}

let filters: HistoryFilters = { entity: '', recordId: '', since: '' }
let events: AuditEvent[] = []
// Customer and people names for the sentences, gathered from every page shown.
let names: HistoryNames = { customers: {}, people: {}, visits: {} }
// Stable event-id cursor: the oldest id shown. The next older page asks for
// everything strictly before it, so rows inserted meanwhile can neither
// shift the window nor duplicate into it.
let cursor: number | null = null
let more = true
let loading = false
let loadSeq = 0 // a newer load (route or filter change) supersedes this one
// What is opened: a change under itself on a phone, the picked change on a computer, and folded runs.
let openRows = new Set<number>()
let picked: number | null = null
let openRuns = new Set<number>()

function onHistoryRoute(): boolean {
  return location.hash === '#/history' || location.hash.startsWith('#/history?')
}

const wide = (): boolean => typeof matchMedia === 'function' && matchMedia('(min-width: 1000px)').matches

function queryParams(append: boolean): string | null {
  const p = new URLSearchParams()
  if (filters.entity) p.set('entity', filters.entity)
  const id = filters.recordId.trim()
  if (id !== '') {
    // Digits alone are not enough: 0 matches no row and an integer beyond
    // Number.MAX_SAFE_INTEGER is not a record id either — both would just
    // buy a server 400. Validate to the same rule the API applies.
    const n = /^\d+$/.test(id) ? Number(id) : 0
    if (!Number.isSafeInteger(n) || n < 1) return null // caller shows the field error
    p.set('entity_id', String(n))
  }
  if (filters.since) p.set('since', filters.since)
  p.set('limit', String(PAGE_LIMIT))
  if (append && cursor !== null) p.set('before_id', String(cursor))
  return `/api/events?${p.toString()}`
}

// A new attempt starts clean: stale failure banners from the previous
// attempt must not stack up above the list.
function clearLoadErrors(): void {
  document.querySelectorAll('#screen .load-error').forEach((n) => n.remove())
}

async function load(append: boolean): Promise<void> {
  const gen = routeGen
  const seq = ++loadSeq
  const qs = queryParams(append)
  const status = document.querySelector<HTMLElement>('#history-status')
  if (!qs) {
    if (!onHistoryRoute() || gen !== routeGen || !status) return
    status.textContent = 'The record number must be a whole number above 0 (or left blank).'
    return
  }
  clearLoadErrors()
  loading = true
  if (status) status.textContent = append ? 'Loading older changes…' : 'Loading history…'
  document.querySelector<HTMLButtonElement>('#history-older')?.setAttribute('disabled', 'disabled')
  try {
    // No cacheKey on purpose — see the header note.
    const data = await api<{ events: AuditEvent[]; names?: HistoryNames }>(qs)
    if (gen !== routeGen || seq !== loadSeq || !onHistoryRoute()) return // stale: a newer route/filter owns the screen
    clearLoadErrors()
    if (data.names) {
      names = {
        customers: { ...(append ? names.customers : {}), ...data.names.customers },
        people: { ...(append ? names.people : {}), ...data.names.people },
        visits: { ...(append ? names.visits : {}), ...data.names.visits },
      }
      // New names (a rename on another phone) reach every sentence, not only the newly loaded ones.
      reads.clear()
    }
    if (append) {
      // Only APPENDING deduplicates (the cursor can re-offer the boundary
      // row); a first-page load REPLACES what is shown with the page as
      // served, so a retry after an older-page failure can never empty the
      // list by filtering out everything already displayed.
      const known = new Set(events.map((e) => e.id))
      events = [...events, ...data.events.filter((e) => !known.has(e.id))]
    } else {
      events = data.events
    }
    cursor = events.length > 0 ? events[events.length - 1].id : null
    more = data.events.length >= PAGE_LIMIT
    loading = false
    renderList()
  } catch (err) {
    if (gen !== routeGen || seq !== loadSeq || !onHistoryRoute()) return
    loading = false
    renderList()
    const root = document.querySelector<HTMLElement>('#screen')
    const listNow = document.querySelector<HTMLElement>('#history-list')
    // Retry repeats the attempt that failed — the SAME page mode (a failed
    // "Load older" must not restart from the top and lose the scroll).
    if (root && listNow) loadError(root, (err as Error).message, () => void load(append))
  }
}

// One change, read once: its values, sentence, who and when.
interface Read {
  before: Record<string, unknown> | null
  after: Record<string, unknown> | null
  changes: FieldChange[]
  sentence: string
  email: string | null
  note: string | null
  clock: string
  day: string
}
const reads = new Map<number, Read>()
function read(ev: AuditEvent): Read {
  let r = reads.get(ev.id)
  if (!r) {
    const before = parseRow(ev.before_json)
    const after = parseRow(ev.after_json)
    const { email, note } = whoDidIt(ev.actor, names)
    const at = eventDayClock(ev.created_at)
    r = { before, after, changes: fieldChanges(ev.action, before, after), sentence: eventSentence(ev, before, after, names), email, note, clock: at?.clock ?? eventTime(ev.created_at), day: at?.day ?? '' }
    reads.set(ev.id, r)
  }
  return r
}

// The small picture for what kind of change it was; Roundly's own changes carry its mark instead (today's green
// edge meant "added", and green means done in the style kit).
function kindDrawing(ev: AuditEvent, r: Read): Drawing {
  const fields = new Set(r.changes.map((c) => c.field))
  if (ev.entity === 'cleans') {
    if (ev.action === 'create') return 'calendarPlus'
    if (ev.action === 'delete') return 'calendarCross'
    if (fields.has('payment_status') || fields.has('payment_method') || fields.has('paid_pence')) return 'pound'
    if (fields.has('state')) return 'tick'
    if (fields.has('date')) return 'calendarMove'
    return 'calendar'
  }
  if (ev.entity === 'customers') return ev.action === 'create' ? 'personPlus' : 'person'
  if (ev.entity === 'leads') return 'personPlus'
  if (ev.entity === 'day_notes') return 'note'
  if (ev.entity === 'day_ledger') return 'pound'
  if (ev.entity === 'gutter_jobs') return 'house'
  if (ev.entity === 'media') return 'camera'
  if (ev.entity === 'customer_jobs') return 'list'
  if (ev.entity === 'price_reviews' || ev.entity === 'price_review_items') return 'priceTag'
  return 'toggle'
}
function ring(ev: AuditEvent, r: Read): HTMLElement {
  if (/^Roundly\b/.test(r.sentence)) return el('span', { class: 'history-ring history-ring-roundly', 'aria-hidden': 'true' }, el('span', { class: 'history-mark' }, 'R.'))
  return el('span', { class: 'history-ring', 'aria-hidden': 'true' }, lineDrawing(kindDrawing(ev, r), 20))
}

function dayWords(day: string): string {
  if (!day) return 'Earlier'
  const today = todayISO()
  const y = new Date(`${today}T12:00:00Z`)
  y.setUTCDate(y.getUTCDate() - 1)
  const yesterday = y.toISOString().slice(0, 10)
  return day === today ? `Today · ${dayLabel(day)}` : day === yesterday ? `Yesterday · ${dayLabel(day)}` : dayLabel(day)
}

// The same change again: same words, same person, same record, same day.
const runKey = (ev: AuditEvent): string => { const r = read(ev); return `${r.sentence}|${ev.actor}|${ev.entity}|${ev.entity_id}|${ev.action}|${r.day}` }

function renderList(): void {
  const list = document.querySelector<HTMLElement>('#history-list')
  const status = document.querySelector<HTMLElement>('#history-status')
  const olderBtn = document.querySelector<HTMLButtonElement>('#history-older')
  if (!list || !status || !olderBtn) return
  clear(list)
  status.textContent = ''
  if (events.length === 0) {
    status.textContent = loading ? '' : 'No changes match these filters yet.'
  }
  // The newest change shown for each visit: the only one Undo is offered on.
  const newest = new Set<number>()
  const seen = new Set<number>()
  for (const ev of events) if (ev.entity === 'cleans' && !seen.has(ev.entity_id)) { seen.add(ev.entity_id); newest.add(ev.id) }
  let day: string | null = null
  let box: HTMLElement | null = null
  for (let i = 0; i < events.length;) {
    const ev = events[i]
    const r = read(ev)
    if (day !== r.day || !box) {
      day = r.day
      box = el('div', { class: 'history-day-list' })
      list.append(el('h3', { class: 'history-day' }, dayWords(r.day)), box)
    }
    // A change that can be undone always stands on its own line, so its Undo is never folded away.
    const undoable = newest.has(ev.id) && undoBodyFor(ev, new Date()) !== null
    let j = i + 1
    if (!undoable) while (j < events.length && runKey(events[j]) === runKey(ev)) j++
    if (j - i >= 3) box.append(renderRun(events.slice(i, j), newest))
    else for (let k = i; k < j; k++) box.append(renderEvent(events[k], newest.has(events[k].id)))
    i = j
  }
  olderBtn.hidden = !more || events.length === 0
  olderBtn.removeAttribute('disabled')
  olderBtn.textContent = more ? 'Load older changes' : 'No more history'
  renderPicked()
}

// Three or more of the same change in a row: one line saying how many and between when, opening to every one.
function renderRun(run: AuditEvent[], newest: Set<number>): HTMLElement {
  const first = run[0]
  const r = read(first)
  const open = openRuns.has(first.id)
  const wrap = el('div', { class: 'history-run' })
  const row = el('button', { type: 'button', class: 'history-row', 'aria-expanded': String(open) }, ring(first, r),
    el('span', { class: 'history-text' },
      el('span', { class: 'history-sentence' }, r.sentence, ' ', el('span', { class: 'history-times' }, `${run.length} times`)),
      el('span', { class: 'history-meta' }, [`${read(run[run.length - 1]).clock} to ${r.clock}`, r.email ?? ''].filter(Boolean).join(' · '))),
    el('span', { class: 'history-chevron' }, lineDrawing('chevronDown', 18)))
  row.addEventListener('click', () => { if (open) openRuns.delete(first.id); else openRuns.add(first.id); renderList() })
  wrap.append(row)
  if (open) wrap.append(el('div', { class: 'history-run-list' }, ...run.map((ev) => renderEvent(ev, newest.has(ev.id)))))
  return wrap
}

// One event, as a person reads it: a plain sentence saying who did what
// ("Lewis marked Anna's visit on Tue 9 Sep paid by bank"), then when and the
// login it was saved from, then the exact values one tap away. Everything is
// a text node built by ui.el — no raw HTML from the server ever enters the
// document.
function renderEvent(ev: AuditEvent, newestForVisit = false): HTMLElement {
  const r = read(ev)
  const card = el('article', { class: `history-event history-event-${ev.action}`, 'data-event-id': String(ev.id) })
  const opens = r.changes.length > 0 || !!r.note || !!customerOf(ev, r)
  const open = wide() ? picked === ev.id : openRows.has(ev.id)
  const text = el('span', { class: 'history-text' }, el('span', { class: 'history-sentence' }, r.sentence),
    el('span', { class: 'history-meta' }, [r.clock, r.email ?? ''].filter(Boolean).join(' · ')))
  const row = opens
    ? el('button', { type: 'button', class: 'history-row', 'aria-expanded': String(open) }, ring(ev, r), text, el('span', { class: 'history-chevron' }, lineDrawing(wide() ? 'chevronRight' : 'chevronDown', 18)))
    : el('div', { class: 'history-row' }, ring(ev, r), text)
  if (opens) row.addEventListener('click', () => {
    if (wide()) picked = picked === ev.id ? null : ev.id
    else if (openRows.has(ev.id)) openRows.delete(ev.id)
    else openRows.add(ev.id)
    renderList()
  })
  const head = el('div', { class: 'history-line' }, row)
  const undo = newestForVisit ? undoBodyFor(ev, new Date()) : null
  if (undo) head.append(undoButton(ev, undo, r, 'Undo'))
  card.append(head)
  if (open) card.classList.add('history-open')
  if (open && !wide()) card.append(details(ev, r))
  return card
}

function undoButton(ev: AuditEvent, body: Record<string, unknown>, r: Read, words: string): HTMLButtonElement {
  const button = el('button', { type: 'button', class: 'btn-secondary history-undo', 'aria-label': `Undo: ${r.sentence}` }, words) as HTMLButtonElement
  button.addEventListener('click', () => void undoEvent(ev, body, button))
  return button
}

// The customer a change is about, for "Open <name>": a customer's own change, or a visit's customer.
function customerOf(ev: AuditEvent, r: Read): { id: number; name: string } | null {
  const row = r.after ?? r.before
  const id = ev.entity === 'customers' ? ev.entity_id : typeof row?.customer_id === 'number' ? row.customer_id : null
  if (!id || ev.action === 'delete' && ev.entity === 'customers') return null
  const name = ev.entity === 'customers' && typeof row?.name === 'string' ? row.name : names.customers[String(id)]
  return name ? { id, name } : null
}

// What changed, in full: every field, before and after (a new record shows what it was given, a removed one what
// it had), the note it was saved with, the login, and a way to the customer.
function details(ev: AuditEvent, r: Read): HTMLElement {
  const box = el('div', { class: 'history-details' })
  if (r.note) box.append(el('p', { class: 'history-note' }, r.note))
  if (r.changes.length > 0) {
    const full = el('ul', { class: 'history-changes history-changes-full' })
    for (const c of r.changes) full.append(changeRow(ev.action, c))
    box.append(full)
  }
  const who = customerOf(ev, r)
  if (who) box.append(el('a', { class: 'history-open-customer', href: `#/customer/${who.id}` }, `Open ${who.name}`, lineDrawing('chevronRight', 16)))
  return box
}

// On a computer: the picked change on the right.
function renderPicked(): void {
  const panel = document.querySelector<HTMLElement>('#history-picked')
  if (!panel) return
  clear(panel)
  const ev = picked === null ? undefined : events.find((e) => e.id === picked)
  if (!ev) {
    panel.append(el('p', { class: 'history-picked-empty muted' }, events.length ? 'Pick a change to see exactly what changed.' : ''))
    return
  }
  const r = read(ev)
  const newest = events.find((e) => e.entity === 'cleans' && e.entity_id === ev.entity_id)?.id === ev.id
  panel.append(el('div', { class: 'history-picked-head' }, ring(ev, r),
    el('div', {}, el('h3', {}, r.sentence), el('p', { class: 'muted' }, [eventTime(ev.created_at), r.email ?? ''].filter(Boolean).join(' · ')))), details(ev, r))
  const undo = ev.entity === 'cleans' && newest ? undoBodyFor(ev, new Date()) : null
  if (undo) panel.append(el('p', { class: 'muted history-picked-undo-words' }, 'Undo puts this visit back as it was before this change.'), undoButton(ev, undo, r, 'Undo this change'))
}

// Puts a visit back as it was before that change (undoBodyFor), then shows History again with the Undo as its newest
// line. A visit changed since is refused by its version and left as it is.
async function undoEvent(ev: AuditEvent, body: Record<string, unknown>, button: HTMLButtonElement): Promise<void> {
  if (body.breakdown_confirm) {
    const c = body.breakdown_confirm as { paid_pence: number; total_pence: number }
    if (!window.confirm(`Already paid ${money(c.paid_pence)}. Undo changes the total to ${money(c.total_pence)}, with ${money(Math.max(0, c.total_pence - c.paid_pence))} still to pay. Payments stay as recorded. Put it back?`)) return
  }
  button.disabled = true
  const path = `/api/cleans/${ev.entity_id}`
  const ack = await mutate({ label: `Undo from History: visit #${ev.entity_id}`, method: 'PATCH', path, body, onConflict: () => {} })
  if (!onHistoryRoute()) return
  if (ack.ok) { toast('Put back as it was before that change.'); window.dispatchEvent(new Event('dropby:saved')); restart(); return }
  if (ack.status === 409) {
    for (const q of getQueue()) if (q.status === 'conflict' && q.path === path) dismiss(q.key)
    toast('That visit has changed since, so it was left as it is. Open it in the diary to change it.')
    button.remove()
    return
  }
  if (ack.status >= 400) { toast('That change could not be undone here. Open the visit in the diary to change it.'); button.disabled = false; return }
  toast('Saved on this phone. It goes through when there is signal.')
}

function changeRow(action: string, c: { label: string; from: string; to: string }): HTMLElement {
  const li = el('li', { class: 'history-change' })
  const shown = c
  li.append(el('span', { class: 'history-field' }, `${c.label}: `))
  if (action === 'create') {
    li.append(el('span', { class: 'history-val' }, shown.to))
  } else if (action === 'delete') {
    li.append(el('span', { class: 'history-val-muted' }, shown.from))
  } else {
    li.append(el('span', { class: 'history-val-muted' }, shown.from), ' → ', el('span', { class: 'history-val' }, shown.to))
  }
  return li
}

function render(root: HTMLElement): void {
  clear(root)
  const about = el('div', { class: 'history-about', id: 'history-about', hidden: 'hidden' },
    el('p', {}, 'Every change to your round, newest first, with who made it and when. Tap a change to see exactly what changed.'),
    el('p', {}, 'Undo is on the newest change to a visit, made by a person in the last day. Changes to customers and settings, visits added or removed, and the changes Roundly makes itself cannot be undone here.'),
    el('p', {}, 'History records the login email. A shared login cannot identify which person made an edit.'))
  const aboutBtn = el('button', { type: 'button', class: 'history-about-btn', 'aria-expanded': 'false', 'aria-controls': 'history-about' }, lineDrawing('info', 18), 'About History')
  aboutBtn.addEventListener('click', () => {
    const show = about.hidden
    about.hidden = !show
    aboutBtn.setAttribute('aria-expanded', String(show))
  })
  root.append(
    el('div', { class: 'page-heading history-heading' }, el('div', {}, el('h2', {}, 'History'), el('p', { class: 'muted' }, 'Every change, newest first.')), aboutBtn),
    about,
  )

  const bar = el('div', { class: 'screen-bar history-bar' })
  const entitySel = el('select', { 'aria-label': 'Filter by type' })
  entitySel.append(el('option', { value: '' }, 'All changes'))
  for (const e of historyEntities(getMe()?.severalJobs === true)) entitySel.append(el('option', { value: e }, entityLabel(e)))
  entitySel.value = filters.entity
  entitySel.addEventListener('change', () => {
    filters.entity = entitySel.value
    restart()
  })

  const idInput = el('input', {
    type: 'text',
    inputmode: 'numeric',
    placeholder: 'Record number',
    'aria-label': 'Filter by record number (optional)',
    class: 'history-id-input',
  })
  idInput.value = filters.recordId
  const applyId = (): void => {
    if (filters.recordId === idInput.value) return
    filters.recordId = idInput.value
    restart()
  }
  idInput.addEventListener('change', applyId)
  idInput.addEventListener('blur', applyId)
  idInput.addEventListener('keydown', e => { if (e.key === 'Enter') applyId() })
  // The record number is rarely needed: behind the search button, open when one is set.
  const idWrap = el('label', { class: 'history-id-wrap' }, idInput)
  idWrap.hidden = !filters.recordId
  const findBtn = el('button', { type: 'button', class: 'history-find', 'aria-label': 'Find a record by its number', 'aria-expanded': String(!idWrap.hidden) }, lineDrawing('search', 20))
  findBtn.addEventListener('click', () => {
    idWrap.hidden = !idWrap.hidden
    findBtn.setAttribute('aria-expanded', String(!idWrap.hidden))
    if (!idWrap.hidden) idInput.focus()
  })

  // On or after this day (the server's "since"), as before.
  const dateInput = el('input', { type: 'date', 'aria-label': 'Show changes on or after' })
  dateInput.value = filters.since
  dateInput.addEventListener('change', () => {
    filters.since = dateInput.value
    restart()
  })
  const since = el('label', { class: `history-since${filters.since ? '' : ' history-since-empty'}` }, el('span', {}, 'Since'), dateInput)

  const reset = el('button', { type: 'button', class: 'history-clear' }, 'Clear filters')
  reset.hidden = !(filters.entity || filters.recordId || filters.since)
  reset.addEventListener('click', () => {
    filters = { entity: '', recordId: '', since: '' }
    render(root)
    restart()
  })

  bar.append(entitySel, since, findBtn, reset, idWrap)
  root.append(bar)
  const older = el('button', { type: 'button', id: 'history-older', class: 'history-older btn-secondary' }, 'Load older changes')
  older.addEventListener('click', () => void load(true))
  root.append(el('div', { class: 'history-columns' },
    el('div', { class: 'history-main' }, el('p', { id: 'history-status', class: 'bar-note', role: 'status' }), el('div', { id: 'history-list', class: 'history-list' }), older),
    el('section', { id: 'history-picked', class: 'history-picked', 'aria-label': 'The change you picked', 'aria-live': 'polite' })))
}

// A filter change is a fresh question: first page, fresh cursor.
function restart(): void {
  events = []
  cursor = null
  more = true
  reads.clear()
  picked = null
  openRows = new Set()
  openRuns = new Set()
  const reset = document.querySelector<HTMLElement>('.history-clear')
  if (reset) reset.hidden = !(filters.entity || filters.recordId || filters.since)
  renderList()
  void load(false)
}

export function mountHistory(): void {
  const root = document.querySelector<HTMLElement>('#screen')!
  names = { customers: {}, people: {}, visits: {} }
  events = []
  cursor = null
  more = true
  reads.clear()
  picked = null
  openRows = new Set()
  openRuns = new Set()
  filters = { entity: '', recordId: '', since: '' }
  render(root)
  renderList()
  void load(false)
}

// A mutation landing elsewhere (retry queue, another screen) extends the
// log — reload the first page so the newest entry shows.
export function historyOnDataChanged(): void {
  if (!onHistoryRoute()) return
  events = []
  cursor = null
  more = true
  reads.clear()
  void load(false)
}
